import { Box, Button, ButtonGroup, Stack, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledButton } from '../../Admin/Communication/Communication.styles';
import { useCandidatePreboarding } from './CandidatePreboardingContext';
import WestTwoToneIcon from '@mui/icons-material/WestTwoTone';

type TabOption = 'candidate-offer' | 'candidate-details' | 'documents' | 'hiring-experience';

const CandidatePreboarding = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOption, setSelectedOption, setMapId, candidateData } = useCandidatePreboarding();
  const isCandidate = !!sessionStorage.getItem("candidate_session");


  useEffect(() => {
    const mapId = location.state?.mapID || null;
    setMapId(mapId);
  }, [location.state]);

  const handleTabClick = (option: TabOption) => {
    setSelectedOption(option);
    navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/${option}`, {
      state: location.state
    });
  }

  useEffect(() => {
    const path = location.pathname.split("/").pop();

    if (location.pathname === `/${tenant}/preboarding/all-candidates/candidate-preboarding`) {
      navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-offer`, {
        replace: true,
        state: location.state
      });
      setSelectedOption("candidate-offer");
    } else if (path && ["candidate-details", "documents", "hiring-experience"].includes(path)) {
      setSelectedOption(path as typeof selectedOption);
    }
  }, [location.pathname, navigate, tenant]);

  const handleNavigateToAllCandidates = () => {
    navigate(`/${tenant}/preboarding/all-candidates`, { replace: true });
  }

  return (
    <Box sx={{ marginLeft: '2.3vw', marginRight: '1.3vw', mt: '0.5vw' }}>
      {
        !isCandidate && (
          <>
            <Stack direction={'row'} spacing={2} alignItems='center' mb={2}>
              <Button
                variant='outlined'
                sx={{
                  width: '4vw',
                  height: '3vw',
                  minWidth: 0,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={handleNavigateToAllCandidates}
                title='Back to All Candidates'
              >
                <WestTwoToneIcon />
              </Button>
              <Typography variant="h5" sx={{ marginBottom: '1vw' }}>
                Candidate Preboarding
              </Typography>
            </Stack>

            <Typography variant="body2" sx={{ marginBottom: '1vw' }}>
              Candidate Name: {candidateData?.candidateName} | Job Title: {candidateData?.jobTitle}
            </Typography>
          </>
        )
      }

      <Box>
        <ButtonGroup variant="outlined" sx={{ maxWidth: '60vw' }}>
          {isCandidate
            ? (
              ["Hired", "BGVRejected"].includes(candidateData?.preboardingStatusKey || "")
                ? (
                  // Only show Hiring Experience for candidate with Hired/BGVRejected
                  <StyledButton
                    variant="outlined"
                    sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                    isselected={selectedOption === 'hiring-experience'}
                    onClick={() => handleTabClick('hiring-experience')}
                  >
                    Hiring Experience
                  </StyledButton>
                )
                : (
                  // Show Offer, Candidate Details, Documents for candidate
                  <>
                    <StyledButton
                      variant="outlined"
                      sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                      isselected={selectedOption === 'candidate-offer'}
                      onClick={() => handleTabClick('candidate-offer')}
                    >
                      Your Offer
                    </StyledButton>
                    <StyledButton
                      variant="outlined"
                      sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                      isselected={selectedOption === 'candidate-details'}
                      onClick={() => handleTabClick('candidate-details')}
                      disabled={!candidateData?.isOfferAccepted}
                    >
                      Candidate Details
                    </StyledButton>
                    <StyledButton
                      variant="outlined"
                      sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                      isselected={selectedOption === 'documents'}
                      onClick={() => handleTabClick('documents')}
                      disabled={!(candidateData?.isOfferAccepted && candidateData?.isProfileComplete)}
                    >
                      Documents
                    </StyledButton>
                  </>
                )
            )
            : (
              // Recruiter: Show all buttons
              <>
                <StyledButton
                  variant="outlined"
                  sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                  isselected={selectedOption === 'candidate-offer'}
                  onClick={() => handleTabClick('candidate-offer')}
                >
                  Offer Letter
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                  isselected={selectedOption === 'candidate-details'}
                  onClick={() => handleTabClick('candidate-details')}
                >
                  Candidate Details
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                  isselected={selectedOption === 'documents'}
                  onClick={() => handleTabClick('documents')}
                >
                  Documents
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  sx={{ mr: 0.13, flexGrow: 1, fontWeight: 500 }}
                  isselected={selectedOption === 'hiring-experience'}
                  onClick={() => handleTabClick('hiring-experience')}
                >
                  Hiring Experience
                </StyledButton>
              </>
            )
          }
        </ButtonGroup>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%', mb: '1.6vw' }}>
        <Outlet />
      </Box>
    </Box>
  )
}

export default CandidatePreboarding